import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMenu, FiX, FiPhone, FiMail } = FiIcons;

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Home', href: '/' },
    { name: 'Gallery', href: '/gallery' },
    { name: 'The Lodge', href: '/lodge' },
    { name: 'Birding', href: '/birding' },
    { name: 'Experience', href: '/experience' },
    { name: 'Contact', href: '/contact' }
  ];

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'glass-strong py-4' : 'py-6'
      }`}
    >
      <div className="container mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center">
            <span className="text-white font-bold text-lg">SB</span>
          </div>
          <div className="hidden md:block">
            <h1 className="font-display text-xl font-bold text-white">
              Sinharaja Birders Lodge
            </h1>
            <p className="text-sm text-gray-300">UNESCO World Heritage Gateway</p>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center space-x-8">
          {navItems.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className={`relative text-white hover:text-green-300 transition-colors duration-300 ${
                location.pathname === item.href ? 'text-green-300' : ''
              }`}
            >
              {item.name}
              {location.pathname === item.href && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute -bottom-1 left-0 right-0 h-0.5 bg-green-300"
                />
              )}
            </Link>
          ))}
        </nav>

        {/* Contact Info & Mobile Menu */}
        <div className="flex items-center space-x-4">
          <div className="hidden md:flex items-center space-x-4">
            <a
              href="tel:+94717714922"
              className="flex items-center space-x-2 text-white hover:text-green-300 transition-colors"
            >
              <SafeIcon icon={FiPhone} className="w-4 h-4" />
              <span className="text-sm">+94 71 771 4922</span>
            </a>
            <a
              href="mailto:info@sinharajabirders.com"
              className="flex items-center space-x-2 text-white hover:text-green-300 transition-colors"
            >
              <SafeIcon icon={FiMail} className="w-4 h-4" />
              <span className="text-sm">Book Direct</span>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-white p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <SafeIcon 
              icon={isMobileMenuOpen ? FiX : FiMenu} 
              className="w-6 h-6" 
            />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden glass-strong mt-4 mx-6 overflow-hidden"
          >
            <div className="p-6 space-y-4">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block text-white hover:text-green-300 transition-colors py-2 ${
                    location.pathname === item.href ? 'text-green-300' : ''
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              <div className="pt-4 border-t border-white/20">
                <a
                  href="tel:+94717714922"
                  className="flex items-center space-x-2 text-white hover:text-green-300 transition-colors py-2"
                >
                  <SafeIcon icon={FiPhone} className="w-4 h-4" />
                  <span>+94 71 771 4922</span>
                </a>
                <a
                  href="mailto:info@sinharajabirders.com"
                  className="flex items-center space-x-2 text-white hover:text-green-300 transition-colors py-2"
                >
                  <SafeIcon icon={FiMail} className="w-4 h-4" />
                  <span>info@sinharajabirders.com</span>
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;